/*
 * Settings for resizing vehicle pictures in the browser before upload (#100).
 * Each one is read from localStorage, then the build variable, then the default, so a tester can change
 * it from the console without a rebuild. The build guard checks the build variables against the same
 * ranges: scripts/check-build-env.js, IMAGE_SETTINGS. Change both together.
 */

export interface ImageUploadSettings {
  enabled: boolean;
  maxWidth: number;
  maxHeight: number;
  quality: number;
  // Null: no cap on the converted file.
  maxBytes: number | null;
}

/*
 * The backend's own rule (bettercargo-inventorydb-backend core/vehicles/models.py, _compress_image):
 * a JPEG within 1920x1080 is stored without being decoded on the server.
 */
export const DEFAULT_IMAGE_UPLOAD_SETTINGS: ImageUploadSettings = {
  enabled: false,
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8,
  maxBytes: null,
};

export const IMAGE_SETTING_RANGES = {
  boxSide: { min: 640, max: 8192 },
  quality: { min: 0.5, max: 0.95 },
  maxBytes: { min: 100000, max: 20000000 },
};

export const IMAGE_SETTING_STORAGE_KEYS = {
  enabled: 'bc.clientImageResize',
  maxBox: 'bc.clientImageMaxBox',
  quality: 'bc.clientImageJpegQuality',
  maxBytes: 'bc.clientImageMaxBytes',
};

export interface ImageBuildEnv {
  REACT_APP_CLIENT_IMAGE_RESIZE?: string;
  REACT_APP_CLIENT_IMAGE_MAX_BOX?: string;
  REACT_APP_CLIENT_IMAGE_JPEG_QUALITY?: string;
  REACT_APP_CLIENT_IMAGE_MAX_BYTES?: string;
}

// Each name spelled out: Create React App inlines only literal process.env.REACT_APP_* references.
const BUILD_ENV: ImageBuildEnv = {
  REACT_APP_CLIENT_IMAGE_RESIZE: process.env.REACT_APP_CLIENT_IMAGE_RESIZE,
  REACT_APP_CLIENT_IMAGE_MAX_BOX: process.env.REACT_APP_CLIENT_IMAGE_MAX_BOX,
  REACT_APP_CLIENT_IMAGE_JPEG_QUALITY: process.env.REACT_APP_CLIENT_IMAGE_JPEG_QUALITY,
  REACT_APP_CLIENT_IMAGE_MAX_BYTES: process.env.REACT_APP_CLIENT_IMAGE_MAX_BYTES,
};

const inRange = (value: number, { min, max }: { min: number; max: number }) => value >= min && value <= max;

export function parseSwitch(value: string): boolean | undefined {
  if (value === 'on') return true;
  if (value === 'off') return false;
  return undefined;
}

// `<width>x<height>` in pixels, each side 640-8192.
export function parseMaxBox(value: string): { width: number; height: number } | undefined {
  const match = /^(\d{1,5})x(\d{1,5})$/.exec(value);
  if (!match) return undefined;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!inRange(width, IMAGE_SETTING_RANGES.boxSide) || !inRange(height, IMAGE_SETTING_RANGES.boxSide)) {
    return undefined;
  }
  return { width, height };
}

// A decimal such as 0.8, 0.5-0.95.
export function parseQuality(value: string): number | undefined {
  if (!/^(\d+(\.\d+)?|\.\d+)$/.test(value)) return undefined;
  const quality = Number(value);
  return inRange(quality, IMAGE_SETTING_RANGES.quality) ? quality : undefined;
}

// Whole bytes, 100000-20000000.
export function parseMaxBytes(value: string): number | undefined {
  if (!/^\d{1,9}$/.test(value)) return undefined;
  const bytes = Number(value);
  return inRange(bytes, IMAGE_SETTING_RANGES.maxBytes) ? bytes : undefined;
}

export interface SettingsStorage {
  getItem(key: string): string | null;
}

function browserStorage(): SettingsStorage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function readStorage(storage: SettingsStorage | undefined, key: string): string | undefined {
  try {
    const value = storage?.getItem(key);
    return value === null || value === undefined || value === '' ? undefined : value;
  } catch {
    return undefined;
  }
}

// Order: localStorage, then the build variable, then the default. An invalid value is skipped with a warning.
function pick<T>(
  parse: (value: string) => T | undefined,
  storage: SettingsStorage | undefined,
  storageKey: string,
  buildName: keyof ImageBuildEnv,
  buildValue: string | undefined,
  fallback: T
): T {
  const stored = readStorage(storage, storageKey);
  if (stored !== undefined) {
    const parsed = parse(stored);
    if (parsed !== undefined) return parsed;
    console.warn(`Image upload settings: ignoring localStorage ${storageKey}=${JSON.stringify(stored)}.`);
  }
  if (buildValue !== undefined && buildValue !== '') {
    const parsed = parse(buildValue);
    if (parsed !== undefined) return parsed;
    console.warn(`Image upload settings: ignoring ${buildName}=${JSON.stringify(buildValue)}.`);
  }
  return fallback;
}

export function resolveImageUploadSettings(
  buildEnv: ImageBuildEnv = BUILD_ENV,
  storage: SettingsStorage | undefined = browserStorage()
): ImageUploadSettings {
  const defaults = DEFAULT_IMAGE_UPLOAD_SETTINGS;
  const keys = IMAGE_SETTING_STORAGE_KEYS;
  const box = pick(
    parseMaxBox,
    storage,
    keys.maxBox,
    'REACT_APP_CLIENT_IMAGE_MAX_BOX',
    buildEnv.REACT_APP_CLIENT_IMAGE_MAX_BOX,
    { width: defaults.maxWidth, height: defaults.maxHeight }
  );
  return {
    enabled: pick(
      parseSwitch,
      storage,
      keys.enabled,
      'REACT_APP_CLIENT_IMAGE_RESIZE',
      buildEnv.REACT_APP_CLIENT_IMAGE_RESIZE,
      defaults.enabled
    ),
    maxWidth: box.width,
    maxHeight: box.height,
    quality: pick(
      parseQuality,
      storage,
      keys.quality,
      'REACT_APP_CLIENT_IMAGE_JPEG_QUALITY',
      buildEnv.REACT_APP_CLIENT_IMAGE_JPEG_QUALITY,
      defaults.quality
    ),
    maxBytes: pick<number | null>(
      parseMaxBytes,
      storage,
      keys.maxBytes,
      'REACT_APP_CLIENT_IMAGE_MAX_BYTES',
      buildEnv.REACT_APP_CLIENT_IMAGE_MAX_BYTES,
      defaults.maxBytes
    ),
  };
}
